import { create } from 'zustand'

interface User {
  id: string
  email: string
  fullName: string
  role: 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER'
  isSuperAdmin?: boolean
}

interface City {
  id: string
  aiqCityId: number
  name: string
  state: string
}

interface AuthState {
  user: User | null
  token: string | null
  cities: City[]
  activeCityId: string | null
  isAuthenticated: boolean
  login: (token: string, user: User, refreshToken?: string) => void
  setUser: (user: User | null) => void
  setCities: (cities: City[]) => void
  setActiveCityId: (cityId: string | null) => void
  logout: () => void
}

function loadJson<T>(key: string): T | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

function syncAuthCookie(): void {
  if (typeof window === 'undefined') return
  const hasToken = !!localStorage.getItem('token')
  const hasCookie = document.cookie.includes('auth_session=')
  if (hasToken && !hasCookie) {
    document.cookie = `auth_session=1; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`
  } else if (!hasToken && hasCookie) {
    document.cookie = 'auth_session=; path=/; max-age=0'
  }
}

// Sync cookie on module load (covers existing sessions)
syncAuthCookie()

export const useAuthStore = create<AuthState>((set) => ({
  user: loadJson<User>('user'),
  token:
    typeof window !== 'undefined' ? localStorage.getItem('token') : null,
  cities: loadJson<City[]>('cities') ?? [],
  activeCityId:
    typeof window !== 'undefined' ? localStorage.getItem('activeCityId') : null,
  isAuthenticated:
    typeof window !== 'undefined' ? !!localStorage.getItem('token') : false,
  login: (token, user, refreshToken) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(user))
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken)
      // Set cookie for Next.js middleware server-side route protection (30d)
      document.cookie = `auth_session=1; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`
    }
    set({ token, user, isAuthenticated: true })
  },
  setUser: (user) => {
    if (typeof window !== 'undefined') {
      if (user) localStorage.setItem('user', JSON.stringify(user))
      else localStorage.removeItem('user')
    }
    set({ user })
  },
  setCities: (cities) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('cities', JSON.stringify(cities))
      // Auto-select first city if none is selected
      const current = localStorage.getItem('activeCityId')
      if ((!current || !cities.some((c) => c.id === current)) && cities.length > 0) {
        localStorage.setItem('activeCityId', cities[0]!.id)
        set({ cities, activeCityId: cities[0]!.id })
        return
      }
    }
    set({ cities })
  },
  setActiveCityId: (cityId) => {
    if (typeof window !== 'undefined') {
      if (cityId) {
        localStorage.setItem('activeCityId', cityId)
      } else {
        localStorage.removeItem('activeCityId')
      }
    }
    set({ activeCityId: cityId })
  },
  logout: () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      localStorage.removeItem('cities')
      localStorage.removeItem('activeCityId')
      localStorage.removeItem('impersonation')
      // Clear auth cookie
      document.cookie = 'auth_session=; path=/; max-age=0'
      window.location.href = '/login'
    }
    set({ user: null, token: null, cities: [], activeCityId: null, isAuthenticated: false })
  },
}))
